<template>
	<app-modal v-if="show" :title="formData.itemName || 'Add New Product'" @close="$emit('close')">
		<div class="mx-auto px-3" style="width: 1000px">
			<div class="flex">
				<div class="flex flex-1 flex-col">
					<marketing-input label-class="w-24" :label="`${$t('itemName')}`" v-model="formData.itemName" />
					<marketing-input
						v-if="selectedRestaurant.showItemCode || !formData.id"
						label-class="w-24"
						:label="`${$t('code')}`"
						v-model="formData.itemCode"
					/>
					<div class="flex items-center">
						<label class="text-13 font-semibold w-24">
							{{ $t('itemDescription') }}
						</label>
						<app-text-area
							name="description"
							class="w-128 ml-1"
							v-model="formData.itemDescription"
							:disabled="pending"
						/>
					</div>
					<div class="flex items-center">
						<marketing-input
							:disabled="!canEditItemPrice"
							container-class="w-full"
							label-class="w-24"
							class="w-20"
							:label="`${$t('itemPrice')}`"
							type="number"
							step="0.01"
							v-model="formData.oldPrice"
						/>
						<div class="w-full flex">
							<label class="text-13 font-semibold mt-1 mr-1">{{ $t('hasDiscount') }}</label>
							<app-checkbox v-model="formData.hasDiscount" />
						</div>
						<div class="w-full">
							<marketing-input
								:disabled="!canEditItemPrice"
								v-if="formData.hasDiscount"
								container-class="w-full"
								label-class="w-32"
								class="w-20"
								:label="`${$t('discountedPrice')}`"
								type="number"
								step="0.01"
								:max="formData.oldPrice"
								v-model="formData.itemPrice"
							/>
						</div>
					</div>
				</div>
				<div class="flex">
					<div class="relative">
						<div class="mb-3">
							<add-icon-component
								:image="
									formData.imageJson && formData.imageJson.stringBase64
										? formData.imageJson.stringBase64
										: $filters.setImage(formData.image)
								"
								:aspect-ratio="10 / 7"
								@on-select="onImageChanged($event)"
								@on-crop="onImageChanged($event)"
								:disabled="pending"
								type="largeBox"
							/>
						</div>
						<div
							v-if="(formData.imageJson && formData.imageJson.stringBase64) || formData.image"
							class="absolute bottom-0 right-0 mb-8 mr-1"
						>
							<app-icon
								icon="search"
								class="text-13 text-muted p-1 border-1 rounded-full cursor-pointer
							hover:bg-gray-300 hover:text-primary"
								@click="
									showImage(
										formData.imageJson && formData.imageJson.stringBase64
											? formData.imageJson.stringBase64
											: $filters.setImage(formData.image)
									)
								"
							/>
						</div>
					</div>
				</div>
			</div>

			<div class="mb-3">
				<marketing-select
					id="category"
					:label="`${$t('itemCategoryName')}`"
					label-class="w-24"
					class="flex-1"
					:canDeselect="false"
					:options="categoriesList"
					:modelValue="(formData.menuRestaurantCategory.id = changedCategory.id)"
					:value="(formData.menuRestaurantCategory.id = changedCategory.id)"
					v-model="formData.menuRestaurantCategory.id"
				/>
			</div>
			<div class="mb-5 flex flex-col">
				<app-checkbox
					container-class="text-base mb-3"
					:label="`${$t('has_specific_timing')}`"
					v-model="specificTiming"
				/>
				<marketing-input
					v-if="specificTiming"
					label-class="w-24"
					:label="`${$t('timing')}`"
					v-model="formData.cookedTimeInMin"
				/>
			</div>
			<div class="mb-5">
				<p class="font-bold mb-3">{{ $t('variations') }}</p>
				<app-checkbox
					container-class="text-base mb-3"
					:label="`${$t('has_variations')}`"
					v-model="formData.hasVariant"
				/>
				<div class="flex flex-col" v-if="formData.hasVariant">
					<div class="flex items-center mb-3">
						<marketing-input
							label-class="w-24"
							:label="`${$t('itemDescription')}`"
							v-model="formData.variant.description"
						/>
						<add-icon-component
							type="xs"
							:image="variantImage"
							:aspect-ratio="10 / 7"
							@on-select="onVariantImageChange($event)"
							@on-crop="onVariantImageChange($event)"
						/>
					</div>
					<div class="flex flex-col">
						<div class="flex items-center justify-between" v-for="(item, i) in variantItems" :key="item.id">
							<marketing-input
								container-class="flex-1"
								label-class="w-24"
								class="w-24"
								:label="`Variation ${i + 1}`"
								v-model="item.itemName"
								:disabled="!isEditingVariant(item.id)"
							/>
							<marketing-input
								container-class="flex-1"
								label-class="w-24"
								class="w-24"
								:label="`${$t('itemDescription')}`"
								v-model="item.description"
								:disabled="!isEditingVariant(item.id)"
							/>
							<marketing-input
								container-class="flex-1"
								label-class="w-16"
								class="w-24"
								:label="`${$t('itemPrice')}`"
								type="number"
								v-model="item.price"
								:disabled="!isEditingVariant(item.id)"
							/>
							<marketing-input
								container-class="flex-1"
								label-class="w-40"
								class="w-20"
								label="Discount Price"
								type="number"
								v-model="item.oldPrice"
								:disabled="!isEditingVariant(item.id)"
							/>
							<app-checkbox
								label="Default"
								:disabled="!isEditingVariant(item.id)"
								v-model="item.isDefault"
							/>
							<div>
								<add-icon-component
									type="xs"
									:image="item.image ? item.image.stringBase64 : $filters.setImage(item.imageUrl)"
									:aspect-ratio="10 / 7"
									@on-select="item.image = { stringBase64: $event }"
									@on-crop="item.image = { stringBase64: $event }"
									:disabled="!isEditingVariant(item.id)"
								/>
							</div>
							<div v-if="formData.variant.id">
								<div v-if="isEditingVariant(item.id)" class="flex">
									<app-button
										class="btn btn-success small transparent ml-2"
										icon="save"
										@click="saveVariation(item)"
									/>
									<app-button
										class="btn btn-secondary small transparent ml-2"
										icon="cancel"
										@click="selectedVariantId = null"
									/>
								</div>
								<div v-else class="flex">
									<app-button
										class="btn btn-info small transparent ml-2"
										icon="pencil"
										@click="selectedVariantId = item.id"
									/>
									<app-button
										class="btn btn-secondary small transparent ml-2"
										icon="trash"
										disabled
										:loading="deletingVariantItem"
										@click="removeVariation(item, i)"
									/>
								</div>
							</div>
							<app-button
								v-else
								class="btn btn-secondary small transparent ml-2"
								icon="trash"
								@click="removeVariation(item, i)"
							/>
						</div>
						<app-button class="btn btn-success transparent" icon="plus" @click="addVariation">{{
							$t('addVariation')
						}}</app-button>
					</div>
				</div>
			</div>
			<div class="mb-5">
				<p class="font-bold mb-3">Toppings</p>
				<app-checkbox
					container-class="text-base mb-3"
					:label="`${$t('has_toppings')}`"
					:disabled="formData.toppingTypes.length"
					v-model="formData.hasToppingTypes"
				/>
				<div class="mt-8 mb-5" v-if="formData.hasToppingTypes">
					<app-select
						:object="true"
						mode="tags"
						placeholder="Selected Toppings"
						v-model="formData.toppingTypes"
						:options="toppings"
						container-class="w-full mt-3"
					/>
				</div>
			</div>
			<div v-if="storeTypeId == 1" class="mb-5">
				<p class="font-bold mb-3">Images</p>
				<div v-if="this.product.id" class="flex flex-1 flex-row overflow-x-auto">
					<div
						v-for="(image, index) in this.formData.photos"
						:key="index"
						:style="
							`background-image: url('${image.imageUrl}'); 
						background-position: center; 
						background-size: contain, cover;
						background-repeat: no-repeat;
						padding: .2rem;
						width: 140px;
						}`
						"
					>
						<app-icon
							icon="cancel"
							style="background-color: white; padding: 5px; border-radius:50%;"
							:class="['text-muted']"
							@click="deleteGalleryImage(this.formData.id, image.id)"
						/>
					</div>
					<div class="flex">
						<add-icon-component
							:image="
								selectedGalleryImg && selectedGalleryImg.stringBase64
									? selectedGalleryImg.stringBase64
									: $filters.setImage(selectedGalleryImg)
							"
							:aspect-ratio="10 / 7"
							:backgroundImage="true"
							@on-select="onGalleryImageSelected($event)"
							@on-crop="onGalleryImageCropped($event)"
						/>
					</div>
				</div>
				<div v-else class="flex flex-1 flex-row overflow-x-auto">
					<div
						v-for="(image, index) in menuItemGallery.photos"
						:key="index"
						:style="
							`background-image: url('${image.imageJson.stringBase64}'); 
						background-position: center; 
						background-size: contain, cover;
						background-repeat: no-repeat;
						padding: .2rem;
						width: 140px;
						}`
						"
					>
						<app-icon
							icon="cancel"
							style="background-color: white; padding: 5px; border-radius:50%;"
							:class="['text-muted']"
							@click="deleteGalleryImage(this.formData.id, index)"
						/>
					</div>
					<div class="flex">
						<add-icon-component
							:image="
								selectedGalleryImg && selectedGalleryImg.stringBase64
									? selectedGalleryImg.stringBase64
									: $filters.setImage(selectedGalleryImg)
							"
							:aspect-ratio="10 / 7"
							:backgroundImage="true"
							@on-select="onGalleryImageSelected($event)"
							@on-crop="onGalleryImageCropped($event)"
						/>
					</div>
				</div>
			</div>
			<div class="flex justify-end mt-2 gap-4">
				<app-button class="btn btn-success w-32" @click="submit" :loading="loading" :disabled="!canEditMenu">{{
					$t('save')
				}}</app-button>
				<app-button
					v-if="!product.id"
					class="btn btn-success transparent"
					@click="submitAndAddNew"
					:loading="loading"
					:disabled="!canEditMenu"
					>{{ $t('save') }} {{ $t('andAddNew') }}</app-button
				>
			</div>
		</div>
	</app-modal>
	<create-topping-modal
		v-if="showToppingModal"
		:show="showToppingModal"
		@close="showToppingModal = false"
		business-name=""
	/>
</template>

<script>
	import AppModal from '@/components/app/AppModal';
	import MarketingInput from '@/components/marketing/forms/formComponents/MarketingInput';
	import AddIconComponent from '@/components/marketing/forms/formComponents/AddIconComponent';
	import MarketingSelect from '@/components/marketing/forms/formComponents/MarketingSelect';
	import AppCheckbox from '@/components/app/AppCheckbox';
	import AppButton from '@/components/app/AppButton';
	import BusinessService from '@/services/sales/BusinessService';
	import MenuService from '@/services/sales/MenuService';
	import { v4 as uuidv4, validate as uuidValidate } from 'uuid';
	import CreateToppingModal from '@/components/sales/toppings/CreateToppingModal';
	import AppSelect from '@/components/app/AppSelect';
	import moment from 'moment';
	import { mapGetters } from 'vuex';
	import AppTextArea from '@/components/app/AppTextArea.vue';

	const DEFAULT_FORM_DATA = {
		menuRestaurantCategory: {},
		variant: { items: [] },
		toppingTypes: [],
		imageJson: {},
		photos: [],
	};

	export default {
		name: 'ProductForm',
		emits: ['create', 'update', 'close', 'createProduct', 'showImage'],
		components: {
			AppTextArea,
			AppSelect,
			CreateToppingModal,
			AppButton,
			AppCheckbox,
			MarketingSelect,
			AddIconComponent,
			MarketingInput,
			AppModal,
		},
		props: {
			menuType: {
				type: Object,
				required: true,
			},
			product: {
				type: Object,
				default: () => ({}),
			},
			categories: {
				type: Array,
				default: () => [],
			},
			show: {
				type: Boolean,
				default: false,
			},
			businessId: {
				type: [Number, String],
				required: true,
			},
			storeTypeId: {
				type: [Number, String],
			},
			changedCategory: {
				type: Object,
			},
			canEditMenu: {
				type: Boolean,
				default: true,
			},
			canEditItemPrice: {
				type: Boolean,
				default: true,
			},
			pending: {
				type: Boolean,
				default: false,
			},
		},
		data: () => ({
			loading: false,
			filters: {
				userSearch: '',
			},
			formData: DEFAULT_FORM_DATA,
			specificTiming: false,
			showToppingModal: false,
			loadingToppings: false,
			toppings: [],
			selectedVariantId: null,
			selectedToppingId: null,
			deletingVariantItem: false,
			menuItemGallery: { photos: [] },
			selectedGalleryImg: null,
		}),
		computed: {
			categoriesList() {
				return this.categories.map((cat) => ({ ...cat, label: cat.menuCategory.categoryName, value: cat.id }));
			},
			variantImage() {
				return this.formData.variant.image && this.formData.variant.image.stringBase64
					? this.formData.variant.image.stringBase64
					: this.$filters.setImage(this.formData.variant.imageUrl);
			},
			variantItems() {
				return this.formData.variant && this.formData.variant.items;
			},
			toppingItems() {
				return this.formData.toppingTypes;
			},
			...mapGetters({
				user: 'user',
			}),
			selectedRestaurant() {
				if (this.$route.params.id) {
					return this.user.restaurants.find((r) => r.restaurantID === Number(this.$route.params.id));
				} else {
					return this.user.restaurants[0];
				}
			},
		},
		methods: {
			getToppings() {
				this.loadingToppings = true;
				BusinessService.getToppings(this.businessId)
					.then((res) => (this.toppings = res))
					.finally(() => (this.loadingToppings = false));
			},
			onImageChanged(stringBase64) {
				this.formData.imageJson = { stringBase64 };
			},
			onGalleryImageSelected(stringBase64) {
				this.selectedGalleryImg = { stringBase64 };
			},
			onGalleryImageCropped(stringBase64) {
				if (!this.formData.itemName) {
					this.$emit('close');
					throw this.$notificationService.error(new Error('Please enter a name for the  new product'));
				}
				this.menuItemGallery.photos.push({
					description: this.formData.itemName.replaceAll(' ', '-') + moment().format('-YYYY-MM-DD-hh-mm-ss'),
					imageJson: { stringBase64 },
				});
				this.loading = true;
				this.product.id
					? MenuService.addImage(this.product.id, this.menuItemGallery)
							.catch((e) => this.$notificationService.error(e))
							.then((data) => {
								this.formData.photos.push({ id: data.photos[0].id, imageUrl: data.photos[0].imageUrl });
								this.menuItemGallery.photos = [];
							})
							.finally(() => (this.loading = false))
					: (this.loading = false);
			},
			onVariantImageChange(stringBase64) {
				this.formData.variant = { ...this.formData.variant, image: { stringBase64 } };
			},
			bindFormData(product) {
				product.toppingTypes = product.toppingTypes.map((top) => ({
					...top,
					label: top.displayedText,
					value: top.id,
				}));
				const hasDiscount = product.itemPrice < product.oldPrice;
				this.formData = {
					...product,
					hasDiscount,
					oldPrice: !hasDiscount ? product.itemPrice : product.oldPrice,
				};
				this.specificTiming = !!product.cookedTimeInMin;
			},
			addVariation() {
				const id = uuidv4();
				this.formData.variant.items
					? this.formData.variant.items.push({ id, isDefault: 0 })
					: (this.formData.variant = { ...this.formData.variant, items: [{ id, isDefault: 0 }] });
				this.selectedVariantId = id;
			},
			saveVariation(variantItem) {
				const isNew = uuidValidate(variantItem.id);
				const payload = {
					...variantItem,
					description: this.formData.variant.description,
				};
				if (isNew) {
					delete payload.id;
				}
				const endpoint = isNew ? MenuService.createVariantItem : MenuService.updateVariantItem;
				endpoint(payload, this.businessId, this.formData.id, this.formData.variant.id)
					.then(() => {
						this.selectedVariantId = null;
						this.$notificationService.success('Variation item saved');
					})
					.catch((e) => {
						this.$notificationService.error(e);
					});
			},
			removeVariation(variant, index) {
				if (uuidValidate(variant.id)) {
					this.formData.variant.items.splice(index, 1);
				} else {
					this.deletingVariantItem = true;
					MenuService.deleteVariantItem(
						this.businessId,
						this.formData.id,
						this.formData.variant.id,
						this.formData.variant.items[index].id
					)
						.then(() => this.formData.variant.items.splice(index, 1))
						.catch((e) => this.$notificationService.error(e))
						.finally(() => (this.deletingVariantItem = false));
				}
			},
			addTopping() {
				this.formData.toppingTypes.push({});
			},
			saveTopping(topping) {
				const isNew = uuidValidate(topping.id);
				console.log(isNew);
			},
			removeTopping(topping, index) {
				if (uuidValidate(topping.id)) {
					this.formData.toppingTypes.splice(index, 1);
				}
			},
			submit() {
				if (this.formData.hasDiscount && this.formData.itemPrice >= this.formData.oldPrice) {
					return this.$notificationService.error('Discounted Price should be lower than normal Price!');
				}
				this.loading = true;
				const endpoint = !this.product.id ? MenuService.createMenuItem : MenuService.updateMenuItem;
				const payload = {
					...this.formData,
					photos: this.menuItemGallery.photos,
					itemPrice: this.formData.hasDiscount ? this.formData.itemPrice : this.formData.oldPrice,
					hasMenuItemExtraDataFrequency: 0,
					menuItemExtraData: { frequencyTypeId: 0 },
				};
				if (payload.variant.id || !payload?.variant?.items?.length) {
					delete payload.variant;
				} else {
					payload.variant.items.forEach((item) => {
						delete item.id;
					});
				}
				if (payload.toppingTypes && !payload.toppingTypes.length) {
					delete payload.toppingTypes;
				}
				if (payload.menuRestaurantCategory.id === -30) {
					payload.menuRestaurantCategory = { id: payload.menuRestaurantCategoryId };
				}
				endpoint(payload, this.businessId, this.menuType.code, this.product.id)
					.then((res) => {
						this.$emit(!this.product.id ? 'create' : 'update', res);
					})
					.catch((e) => this.$notificationService.error(e))
					.then(() => {
						this.loading = false;
					});
			},
			submitAndAddNew() {
				if (this.formData.hasDiscount && this.formData.itemPrice >= this.formData.oldPrice) {
					return this.$notificationService.error('Discounted Price should be lower than normal Price!');
				}
				this.loading = true;
				const endpoint = !this.product.id ? MenuService.createMenuItem : MenuService.updateMenuItem;
				const payload = {
					...this.formData,
					photos: this.menuItemGallery.photos,
					itemPrice: this.formData.hasDiscount ? this.formData.itemPrice : this.formData.oldPrice,
				};
				if (payload.variant.id || !payload?.variant?.items?.length) {
					delete payload.variant;
				} else {
					payload.variant.items.forEach((item) => {
						delete item.id;
					});
				}
				if (payload.toppingTypes && !payload.toppingTypes.length) {
					delete payload.toppingTypes;
				}
				endpoint(payload, this.businessId, this.menuType.code, this.product.id)
					.then((res) => {
						this.$emit(!this.product.id ? 'create' : 'update', res);
					})
					.catch((e) => this.$notificationService.error(e))
					.then(() => {
						this.loading = false;
						this.menuItemGallery.photos = [];
						this.$emit('createProduct');
					});
			},
			isEditingVariant(id) {
				return this.selectedVariantId === id || !this.formData?.variant?.id;
			},
			isEditingTopping(id) {
				return this.selectedToppingId === id || !this.formData.id;
			},
			deleteGalleryImage(menuItemId, imageId) {
				if (this.product.id) {
					this.loading = true;
					MenuService.deleteImage(menuItemId, imageId)
						.then(() => {
							this.formData.photos = this.formData.photos.filter((obj) => obj.id !== imageId);
						})
						.catch((e) => {
							this.$notificationService.error(e);
						})
						.finally(() => {
							this.loading = false;
						});
				} else
					this.menuItemGallery.photos = this.menuItemGallery.photos.filter(
						(obj) => this.menuItemGallery.photos.indexOf(obj) !== imageId
					);
			},
			showImage(image) {
				this.$emit('showImage', image);
			},
		},
		mounted() {
			this.bindFormData({ ...DEFAULT_FORM_DATA, ...this.product });
			this.getToppings();
		},
		watch: {
			product(val) {
				this.bindFormData({ ...DEFAULT_FORM_DATA, ...val });
			},
			specificTiming(newVal) {
				if (!newVal) {
					this.formData.cookedTimeInMin = 0;
				}
			},
		},
	};
</script>

<style lang="scss" scoped>
	:deep(.modal-body) {
		max-height: 90vh;
	}
</style>
